import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BASE_URL } from "../../const";
import Navbar from "../../components/Navbar";
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];
const AUDIT_OPTIONS = [
  { v: "pending", l: "Pending" },
  { v: "missing_values", l: "Missing Values" },
  { v: "completed", l: "Completed" },
];

const DEFAULT_FILTERS = {
  search: "",
  skuStatus: "",
  skuType: "",
  businessType: "",
  comboOf: "",
  skuTag: "",
  costAudit: "",
  attrAudit: "",
  category: "",
  compLevel: false,
};

const DECIMAL_FIELDS = new Set([
  "tax_rate", "mrp", "sku_cost", "sku_cost_tax",
  "sku_inner_packaging_w_tax", "sku_outer_packaging_w_tax",
  "mile_1_cost_w_tax", "mile_2_cost_w_tax", "processing_cost_w_tax",
  "sku_actual_weight_in_kg", "sku_courier_push_weight",
]);

const CSV_FIELDS = [
  "id", "business_type", "parent_sku", "sku", "sku_name", "sku_reference_code",
  "sku_type", "ee_category", "sku_dimensions", "sku_courier_push_weight",
  "ean_upc", "hsn_code", "tax_rate", "mrp", "product_image_url", "components",
  "category", "sub_category", "color", "pot", "sku_size", "sku_actual_dimensions",
  "sku_actual_weight_in_kg", "combo_of", "sku_cost", "sku_cost_tax",
  "sku_inner_packaging_w_tax", "sku_outer_packaging_w_tax",
  "mile_1_cost_w_tax", "mile_2_cost_w_tax", "processing_cost_w_tax",
  "sku_status", "sku_tag", "cost_audit", "attr_audit", "created_at", "updated_at",
];

const IMPORTABLE_FIELDS = new Set([
  "id", "sku", "business_type", "parent_sku", "sku_reference_code", "sku_name",
  "sku_type", "combo_of", "category", "ee_category", "sub_category", "color",
  "pot", "sku_size", "sku_dimensions", "sku_actual_dimensions",
  "sku_actual_weight_in_kg", "sku_courier_push_weight", "ean_upc", "hsn_code",
  "tax_rate", "mrp", "sku_cost", "sku_cost_tax", "sku_inner_packaging_w_tax",
  "sku_outer_packaging_w_tax", "mile_1_cost_w_tax", "mile_2_cost_w_tax",
  "processing_cost_w_tax", "product_image_url", "components", "component_skus",
  "sku_tag", "sku_status", "cost_audit", "attr_audit", "sku_created_date",
]);

const TABLE_COLUMNS = [
  { key: "id",                        label: "ID",                  editable: false, width: 70  },
  { key: "business_type",             label: "Business Type",       editable: true,  width: 140 },
  { key: "parent_sku",                label: "Parent SKU",          editable: true,  width: 140 },
  { key: "sku",                       label: "SKU",                 editable: false, width: 140 },
  { key: "sku_name",                  label: "Name",                editable: true,  width: 250 },
  { key: "sku_reference_code",        label: "Ref SKU",             editable: true,  width: 140 },
  { key: "sku_type",                  label: "SKU Type",            editable: false, width: 130 },
  { key: "ee_category",               label: "EE Category",         editable: true,  width: 140 },
  { key: "sku_dimensions",            label: "Dimensions",          editable: true,  width: 140 },
  { key: "sku_courier_push_weight",   label: "Courier Weight (kg)", editable: true,  width: 150 },
  { key: "ean_upc",                   label: "EAN/UPC",             editable: true,  width: 140 },
  { key: "hsn_code",                  label: "HSN Code",            editable: true,  width: 120 },
  { key: "tax_rate",                  label: "Tax %",               editable: true,  width: 100 },
  { key: "mrp",                       label: "MRP",                 editable: true,  width: 100 },
  { key: "product_image_url",         label: "Image URL",           editable: true,  width: 250 },
  { key: "components",                label: "Components",          editable: false, width: 100 },
  { key: "category",                  label: "Category",            editable: true,  width: 150 },
  { key: "sub_category",              label: "Sub Category",        editable: true,  width: 140 },
  { key: "color",                     label: "Color",               editable: true,  width: 110 },
  { key: "pot",                       label: "Pot",                 editable: true,  width: 110 },
  { key: "sku_size",                  label: "Size",                editable: true,  width: 110 },
  { key: "sku_actual_dimensions",     label: "Actual Dimensions",   editable: true,  width: 160 },
  { key: "sku_actual_weight_in_kg",   label: "Weight (kg)",         editable: true,  width: 140 },
  { key: "combo_of",                  label: "Combo Qty",           editable: true,  width: 120 },
  { key: "sku_cost",                  label: "Cost",                editable: true,  width: 100 },
  { key: "sku_cost_tax",              label: "Cost Tax",            editable: true,  width: 120 },
  { key: "sku_inner_packaging_w_tax", label: "Inner Packaging",     editable: true,  width: 160 },
  { key: "sku_outer_packaging_w_tax", label: "Outer Packaging",     editable: true,  width: 160 },
  { key: "mile_1_cost_w_tax",         label: "Mile 1 Cost",         editable: true,  width: 140 },
  { key: "mile_2_cost_w_tax",         label: "Mile 2 Cost",         editable: true,  width: 140 },
  { key: "processing_cost_w_tax",     label: "Processing Cost",     editable: true,  width: 160 },
  { key: "sku_status",                label: "Status",              editable: false, width: 120 },
  { key: "sku_tag",                   label: "Tag",                 editable: true,  width: 120 },
  { key: "cost_audit",                label: "Cost Audit",          editable: false, width: 140 },
  { key: "attr_audit",                label: "Attr Audit",          editable: false, width: 140 },
  { key: "created_at",                label: "Created At",          editable: false, width: 180 },
  { key: "updated_at",                label: "Updated At",          editable: false, width: 180 },
];

const DEFAULT_VISIBLE = [
  "id", "business_type", "parent_sku", "sku", "sku_name", "sku_reference_code",
  "sku_type", "ean_upc", "hsn_code", "tax_rate", "mrp", "components", "category",
  "sub_category", "color", "pot", "sku_size", "sku_actual_dimensions",
  "sku_actual_weight_in_kg", "combo_of", "sku_cost", "sku_cost_tax",
  "sku_inner_packaging_w_tax", "sku_outer_packaging_w_tax",
  "mile_1_cost_w_tax", "mile_2_cost_w_tax", "processing_cost_w_tax",
  "sku_status", "sku_tag", "cost_audit", "attr_audit", "created_at", "updated_at",
];

// ─── API helpers ──────────────────────────────────────────────────────────────

function getAuthHeader() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return {};
    if (token.startsWith("Bearer ") || token.startsWith("Token ")) return { Authorization: token };
    return { Authorization: `Token ${token}` };
  } catch { return {}; }
}

function parseListResponse(data) {
  let rows = [];
  if (Array.isArray(data?.results)) rows = data.results;
  else if (Array.isArray(data?.data)) rows = data.data;
  else if (Array.isArray(data)) rows = data;

  return rows.map(item => {
    const categoryFromLegacyObject =
      item?.category_detail && typeof item.category_detail === "object"
        ? item.category_detail.category : null;
    const normalizedCategory =
      typeof item?.category === "string"
        ? item.category
        : categoryFromLegacyObject ?? (item?.category ?? "");
    return {
      ...item,
      cost_audit: item?.cost_audit ?? item?.cost_audit_status ?? "",
      attr_audit: item?.attr_audit ?? item?.attribute_audit_status ?? "",
      category: normalizedCategory,
      components_raw: Array.isArray(item.components) ? item.components : item.components ?? null,
      components: Array.isArray(item.components)
        ? item.components.map(c => `${c.comp_sku} (${c.comp_qty})`).join(", ")
        : "",
    };
  });
}

async function fetchSkus({ page, pageSize, filters, sort }) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (filters.search)    params.set("search", filters.search);
  if (filters.skuStatus) params.set("sku_status", filters.skuStatus);
  if (filters.skuType)   params.set("sku_type", filters.skuType);
  if (filters.businessType) params.set("business_type", filters.businessType);
  if (filters.comboOf)   params.set("combo_of", filters.comboOf);
  if (filters.skuTag === "__blank__") params.set("sku_tag", "__blank__");
  else if (filters.skuTag) params.set("sku_tag", filters.skuTag);
  if (filters.costAudit) params.set("cost_audit", filters.costAudit);
  if (filters.attrAudit) params.set("attr_audit", filters.attrAudit);
  if (filters.category)  params.set("category", filters.category);
  if (filters.compLevel) params.set("comp_lvl", "yes");
  if (sort?.key) params.set("ordering", `${sort.dir === "desc" ? "-" : ""}${sort.key}`);

  const response = await fetch(`${BASE_URL}/sku/?${params.toString()}`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Failed to fetch SKU data");
  return {
    rows: parseListResponse(data),
    count: Number(data?.count || 0),
    next: data?.next || null,
    previous: data?.previous || null,
  };
}

async function fetchCategories() {
  const response = await fetch(`${BASE_URL}/categories/`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Failed to fetch categories");
  return parseListResponse(data);
}

async function fetchDropdownOptions(fieldName) {
  const params = new URLSearchParams({ field_name: fieldName });
  const response = await fetch(`${BASE_URL}/dropdowns/options/?${params.toString()}`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || `Failed to fetch ${fieldName} options`);
  return parseListResponse(data)
    .map(row => ({ v: String(row?.value ?? "").trim(), l: String(row?.label ?? row?.value ?? "").trim() }))
    .filter(row => row.v);
}

async function bulkUpdateSkus(payload) {
  const response = await fetch(`${BASE_URL}/sku/bulk-update/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
    body: JSON.stringify(payload),
  });
  const contentType = String(response.headers.get("content-type") || "").toLowerCase();
  if (contentType.includes("text/csv")) {
    const blob = await response.blob();
    const filename = filenameFromDisposition(response.headers.get("content-disposition"));
    downloadBlob(blob, filename);
    return { downloadedCsv: true, filename };
  }
  let data = null;
  try { data = await response.json(); } catch (_e) { data = null; }
  if (!response.ok) throw new Error(data?.message || data?.error || `Bulk update failed (HTTP ${response.status})`);
  return data || {};
}

async function bulkDuplicateSkusByBusinessType(payload) {
  const response = await fetch(`${BASE_URL}/sku/bulk-duplicate-by-business-type/`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Bulk duplicate failed");
  return data;
}

// ─── CSV utilities ────────────────────────────────────────────────────────────

function csvSafe(v) {
  const s = String(v ?? "");
  return (s.includes(",") || s.includes('"') || s.includes("\n"))
    ? `"${s.replace(/"/g, '""')}"` : s;
}
function toCsv(rows) {
  const header = CSV_FIELDS.join(",");
  const body = rows.map(r =>
    CSV_FIELDS.map(f => {
      if (f === "components") return csvSafe(JSON.stringify(r.components_raw ?? r.components ?? ""));
      if (f === "component_skus") {
        if (Array.isArray(r.component_skus)) return csvSafe(r.component_skus.join(", "));
        if (Array.isArray(r.components_raw)) return csvSafe(r.components_raw.map(c => c.comp_sku).join(", "));
        return csvSafe(r.component_skus ?? "");
      }
      return csvSafe(r[f]);
    }).join(",")
  ).join("\n");
  return `${header}\n${body}`;
}
function downloadCsv(content, filename) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}
function filenameFromDisposition(disposition, fallback = "sku_bulk_update_result.csv") {
  if (!disposition) return fallback;
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) { try { return decodeURIComponent(utfMatch[1]); } catch (_e) { return utfMatch[1]; } }
  const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
  if (asciiMatch?.[1]) return asciiMatch[1];
  return fallback;
}
function parseCSV(text) {
  const rows = []; let cur = "", line = [], inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i], nx = text[i + 1];
    if (ch === '"' && inQ && nx === '"') { cur += '"'; i++; continue; }
    if (ch === '"') { inQ = !inQ; continue; }
    if (ch === ',' && !inQ) { line.push(cur); cur = ""; continue; }
    if ((ch === '\n' || ch === '\r') && !inQ) {
      if (ch === '\r' && nx === '\n') i++;
      line.push(cur); rows.push(line); line = []; cur = ""; continue;
    }
    cur += ch;
  }
  if (cur.length > 0 || line.length > 0) { line.push(cur); rows.push(line); }
  return rows;
}
function normalizeUpload(key, val) {
  const t = String(val ?? "").trim();
  if (!t) return null;
  if (key === "id") return /^\d+$/.test(t) ? Number(t) : t;
  if (key === "components") {
    if (t.startsWith("{") || t.startsWith("[")) { try { return JSON.parse(t); } catch { } }
    return t;
  }
  return t;
}
function mapCsvToPayload(text) {
  const matrix = parseCSV(text);
  if (!matrix.length) throw new Error("CSV file is empty");
  const headers = matrix[0].map(h => String(h || "").trim());
  if (!headers.includes("id")) throw new Error("CSV must have 'id' column");
  return matrix.slice(1)
    .filter(l => l.some(c => String(c || "").trim() !== ""))
    .map(l => {
      const obj = {};
      headers.forEach((k, i) => { if (IMPORTABLE_FIELDS.has(k)) obj[k] = normalizeUpload(k, l[i]); });
      return obj;
    }).filter(r => r.id);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getBulkValue(key, val) {
  if (val === undefined) return undefined;
  if (DECIMAL_FIELDS.has(key)) { const t = String(val ?? "").trim(); return t === "" ? null : t; }
  if (typeof val === "string") { const t = val.trim(); return t === "" ? null : t; }
  return val ?? null;
}
function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

// ─── Shared style ─────────────────────────────────────────────────────────────

const SELECT_CLS = "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer";

// ─── Toast ────────────────────────────────────────────────────────────────────

const Toast = memo(({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  const wrapperCls = {
    success: "border-green-500 bg-green-50 text-green-800",
    error:   "border-red-500 bg-red-50 text-red-800",
    warning: "border-yellow-500 bg-yellow-50 text-yellow-800",
    info:    "border-blue-500 bg-blue-50 text-blue-800",
  };
  const dotCls = {
    success: "bg-green-500", error: "bg-red-500",
    warning: "bg-yellow-500", info: "bg-blue-500",
  };
  return (
    <div className={cn(
      "fixed bottom-6 right-6 z-[9999] flex items-center gap-3 max-w-sm",
      "rounded-lg border-2 px-4 py-3 shadow-xl text-sm",
      wrapperCls[type] || wrapperCls.info
    )}>
      <span className={cn("w-2 h-2 rounded-full shrink-0", dotCls[type] || dotCls.info)} />
      <span className="flex-1">{message}</span>
      <button onClick={onClose} className="shrink-0 opacity-60 hover:opacity-100 transition-opacity">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
});

// ─── EditableCell ──────────────────────────────────────────────────────────────

const EditableCell = memo(({ value, rowId, colKey, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));
  const ref = useRef();

  const commit = useCallback(() => {
    setEditing(false);
    if (draft !== String(value ?? "")) onChange(rowId, colKey, draft);
  }, [draft, value, rowId, colKey, onChange]);

  if (editing) return (
    <Input
      ref={ref}
      autoFocus
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") { setDraft(String(value ?? "")); setEditing(false); }
      }}
      className="h-7 text-xs py-0 px-1.5"
    />
  );

  const display = colKey.endsWith("_at") ? fmtDate(value) : (value ?? "—");
  return (
    <span
      onClick={() => { setDraft(String(value ?? "")); setEditing(true); }}
      title="Click to edit"
      className={cn(
        "block w-full cursor-text truncate px-0.5 py-0.5 rounded text-sm transition-colors hover:bg-accent",
        value ? "text-foreground" : "text-muted-foreground"
      )}
    >
      {String(display)}
    </span>
  );
});

// ─── SortIcon ─────────────────────────────────────────────────────────────────

function SortIcon({ dir }) {
  if (dir === "asc")  return <ArrowUp   className="w-3 h-3 text-primary shrink-0" />;
  if (dir === "desc") return <ArrowDown className="w-3 h-3 text-primary shrink-0" />;
  return <ArrowUpDown className="w-3 h-3 text-muted-foreground/40 shrink-0" />;
}

// ─── StatusBadge ──────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  active:         "bg-green-100 text-green-700 border-green-200",
  inactive:       "bg-red-100 text-red-700 border-red-200",
  discontinued:   "bg-yellow-100 text-yellow-700 border-yellow-200",
  approved:       "bg-blue-100 text-blue-700 border-blue-200",
  pending:        "bg-yellow-100 text-yellow-700 border-yellow-200",
  missing_values: "bg-red-100 text-red-700 border-red-200",
  completed:      "bg-green-100 text-green-700 border-green-200",
  rejected:       "bg-red-100 text-red-700 border-red-200",
};

function StatusBadge({ value }) {
  if (!value) return <span className="text-muted-foreground text-xs">—</span>;
  const label = String(value).replace(/_/g, " ");
  const style = STATUS_STYLES[String(value).toLowerCase()];
  if (!style) return <span className="text-muted-foreground text-xs capitalize">{label}</span>;
  return (
    <Badge className={cn("capitalize text-xs font-medium", style)}>
      {label}
    </Badge>
  );
}

// ─── ResizeHandle ─────────────────────────────────────────────────────────────

const ResizeHandle = ({ onMouseDown }) => (
  <div
    onMouseDown={onMouseDown}
    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 transition-colors"
  />
);

// ─── ActionsModal ─────────────────────────────────────────────────────────────

function ActionsModal({ onClose, selectedRows, onUpdateSelected, onUploadComplete, showMessage }) {
  const [busy, setBusy] = useState({ downloading: false, uploading: false, duplicating: false, updating: false });
  const [dupPopup, setDupPopup] = useState(false);
  const [dupBiz, setDupBiz] = useState("");
  const isBusy = Object.values(busy).some(Boolean);
  const set = k => v => setBusy(p => ({ ...p, [k]: v }));

  const dlSelected = async () => {
    if (isBusy) return;
    if (!selectedRows.length) { showMessage("Select at least one row.", "warning"); return; }
    set("downloading")(true);
    try {
      downloadCsv(toCsv(selectedRows), `sku_selected_${new Date().toISOString().slice(0, 10)}.csv`);
      showMessage(`Downloaded ${selectedRows.length} rows.`, "success");
    } finally { set("downloading")(false); }
  };

  const upload = async e => {
    const file = e.target.files?.[0];
    if (!file || isBusy) { e.target.value = ""; return; }
    set("uploading")(true);
    try {
      const payload = mapCsvToPayload(await file.text());
      if (!payload.length) { showMessage("No valid rows found.", "warning"); return; }
      const res = await bulkUpdateSkus(payload);
      if (res?.downloadedCsv) {
        showMessage("Processed with row errors. Downloaded error CSV.", "warning");
        onUploadComplete(); return;
      }
      const s = res?.data || res || {};
      const updated = Number(s.updated ?? s.updated_count ?? 0);
      const failed = Number(s.failed ?? 0);
      showMessage(`Updated: ${updated}, Failed: ${failed}`, failed ? "warning" : "success");
      onUploadComplete();
    } catch (err) { showMessage(err.message || "Upload failed", "error"); }
    finally { set("uploading")(false); e.target.value = ""; }
  };

  const duplicate = async () => {
    const bt = dupBiz.trim();
    if (!bt) { showMessage("Enter a business type.", "warning"); return; }
    if (!selectedRows.length) { showMessage("Select at least one row.", "warning"); return; }
    set("duplicating")(true);
    try {
      const payload = {
        items: selectedRows.filter(r => r?.id).map(r => ({ id: r.id, business_type: bt })),
      };
      const res = await bulkDuplicateSkusByBusinessType(payload);
      const s = res?.data || {};
      const skuS = s?.sku || {};
      const compS = s?.components || {};
      const rejected = Number(skuS.rejected || 0) + Number(compS.rejected || 0);
      showMessage(
        `SKU created: ${Number(skuS.created || 0)}, rejected: ${Number(skuS.rejected || 0)} — ` +
        `Components unique: ${Number(compS.unique_components || 0)}, created: ${Number(compS.created || 0)}, rejected: ${Number(compS.rejected || 0)}`,
        rejected ? "warning" : "success"
      );
      setDupPopup(false); setDupBiz(""); onUploadComplete();
    } catch (err) { showMessage(err.message || "Duplicate failed", "error"); }
    finally { set("duplicating")(false); }
  };

  const updateSel = async () => {
    if (!selectedRows.length) { showMessage("Select at least one row.", "warning"); return; }
    set("updating")(true);
    try {
      const s = await onUpdateSelected(selectedRows);
      if (s?.downloadedCsv) {
        showMessage("Processed with errors. Downloaded error CSV.", "warning");
      } else {
        showMessage(`Updated: ${s?.updated || 0}, Failed: ${s?.failed || 0}`, s?.failed ? "warning" : "success");
      }
      onUploadComplete();
    } catch (err) { showMessage(err.message || "Update failed", "error"); }
    finally { set("updating")(false); }
  };

  return (
    <div
      className="fixed z-[5000]"
      style={{ top: 140, right: "max(20px, calc((100vw - 1600px) / 2 + 20px))" }}
    >
      <div className="bg-white rounded-2xl border border-border shadow-2xl p-6 w-[420px] max-w-[92vw]">
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-foreground">Actions</h3>
          {!isBusy && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Download, duplicate, or bulk-update SKU rows.
        </p>

        {/* Action buttons */}
        <div className="flex flex-col gap-2.5">
          <Button onClick={dlSelected} disabled={isBusy} className="w-full justify-start gap-2">
            {busy.downloading && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy.downloading ? "Downloading…" : "Download Selected"}
          </Button>
          {selectedRows.length > 0 && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { if (!selectedRows.length) { showMessage("Select rows.", "warning"); return; } setDupBiz(""); setDupPopup(true); }}
                disabled={isBusy}
                className="flex-1 gap-1"
              >
                {busy.duplicating && <Loader2 className="h-3 w-3 animate-spin" />}
                {busy.duplicating ? "Duplicating…" : `Duplicate (${selectedRows.length})`}
              </Button>
              <Button onClick={updateSel} disabled={isBusy} className="flex-1 gap-1">
                {busy.updating && <Loader2 className="h-3 w-3 animate-spin" />}
                {busy.updating ? "Updating…" : `Update Selected (${selectedRows.length})`}
              </Button>
            </div>
          )}
        </div>

        {/* Busy indicator */}
        {isBusy && (
          <div className="mt-4 flex items-center gap-2 text-sm text-primary">
            <Loader2 className="h-4 w-4 animate-spin" />
            {busy.uploading ? "Uploading…" : busy.downloading ? "Downloading…" : busy.duplicating ? "Duplicating…" : "Updating…"}
          </div>
        )}

        {/* Duplicate popup */}
        {dupPopup && (
          <div className="mt-4 p-4 bg-violet-50 rounded-xl border border-violet-200">
            <p className="text-sm font-semibold text-violet-700 mb-3">Duplicate Rows</p>
            <Input
              type="text"
              placeholder="New business type (e.g. b2b2c)"
              value={dupBiz}
              disabled={isBusy}
              onChange={e => setDupBiz(e.target.value)}
              className="mb-3"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setDupPopup(false); setDupBiz(""); }} disabled={isBusy} className="flex-1">
                Cancel
              </Button>
              <Button onClick={duplicate} disabled={isBusy} className="flex-1 gap-1">
                {busy.duplicating && <Loader2 className="h-3 w-3 animate-spin" />}
                Confirm
              </Button>
            </div>
          </div>
        )}

        {/* CSV upload */}
        <div className="mt-4 p-3 bg-muted/40 rounded-lg border border-dashed border-border">
          <label className={cn("text-sm text-muted-foreground font-medium cursor-pointer", isBusy && "opacity-50 pointer-events-none")}>
            <span className="flex items-center gap-2 mb-2">
              📎 Upload CSV for bulk update
              {busy.uploading && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
            </span>
            <input
              type="file"
              accept=".csv,text/csv"
              disabled={isBusy}
              onChange={upload}
              className="text-xs block"
            />
          </label>
        </div>
      </div>
    </div>
  );
}

// ─── FilterPanel ──────────────────────────────────────────────────────────────

const FilterPanel = memo(({ filters, setFilters, categories, businessTypeOptions, skuTagOptions, onApply, onClear }) => {
  const activeChips = Object.entries(filters)
    .filter(([k, v]) => k !== "compLevel" ? !!v : false)
    .map(([k, v]) => ({ k, v }));

  const selectFilters = [
    { key: "businessType", label: "Business Type", opts: businessTypeOptions },
    { key: "skuType",      label: "SKU Type",      opts: [{ v: "single_sku", l: "Single" }, { v: "combo_product", l: "Bundle" }] },
    { key: "skuStatus",    label: "Status",        opts: [{ v: "active", l: "Active" }, { v: "inactive", l: "Inactive" }] },
    { key: "costAudit",    label: "Cost Audit",    opts: [{ v: "pending", l: "Pending" }, { v: "missing_values", l: "Missing Values" }, { v: "completed", l: "Completed" }] },
    { key: "attrAudit",    label: "Attr Audit",    opts: [{ v: "pending", l: "Pending" }, { v: "missing_values", l: "Missing Values" }, { v: "completed", l: "Completed" }] },
  ];

  return (
    <div className="bg-white border border-border rounded-xl p-5 mb-3">
      {/* Active filter chips */}
      {(activeChips.length > 0 || filters.compLevel) && (
        <div className="flex flex-wrap gap-2 mb-4">
          {activeChips.map(({ k, v }) => (
            <span key={k} className="inline-flex items-center gap-1.5 bg-violet-100 text-violet-700 rounded-full px-3 py-0.5 text-xs font-medium">
              {k.replace(/_/g, " ")}: {v}
              <button onClick={() => setFilters(p => ({ ...p, [k]: "" }))} className="hover:text-violet-900 transition-colors">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {filters.compLevel && (
            <span className="inline-flex items-center gap-1.5 bg-violet-100 text-violet-700 rounded-full px-3 py-0.5 text-xs font-medium">
              Include components
              <button onClick={() => setFilters(p => ({ ...p, compLevel: false }))} className="hover:text-violet-900 transition-colors">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Filter grid */}
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))" }}>
        {selectFilters.map(({ key, label, opts }) => (
          <div key={key} className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</label>
            <select value={filters[key]} onChange={e => setFilters(p => ({ ...p, [key]: e.target.value }))} className={SELECT_CLS}>
              <option value="">All</option>
              {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
        ))}

        {/* Category */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Category</label>
          <select value={filters.category} onChange={e => setFilters(p => ({ ...p, category: e.target.value }))} className={SELECT_CLS}>
            <option value="">All categories</option>
            {categories.map(c => {
              const value = c?.category ?? c?.value ?? "";
              const label = c?.category ?? c?.label ?? value;
              if (!value) return null;
              return <option key={`${c?.id ?? value}`} value={value}>{label}</option>;
            })}
          </select>
        </div>

        {/* Tag */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tag</label>
          <select value={filters.skuTag} onChange={e => setFilters(p => ({ ...p, skuTag: e.target.value }))} className={SELECT_CLS}>
            <option value="">All</option>
            {skuTagOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>

        {/* Combo Qty */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Combo Qty</label>
          <Input type="text" placeholder="e.g. 3" value={filters.comboOf} onChange={e => setFilters(p => ({ ...p, comboOf: e.target.value }))} />
        </div>

        {/* Include components */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">&nbsp;</label>
          <label className="flex items-center gap-2.5 cursor-pointer py-2">
            <Checkbox
              checked={filters.compLevel}
              onCheckedChange={checked => setFilters(p => ({ ...p, compLevel: !!checked }))}
            />
            <span className="text-sm text-foreground">Include components</span>
          </label>
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <Button size="sm" onClick={onApply}>Apply filters</Button>
        <Button size="sm" variant="outline" onClick={onClear}>Clear all</Button>
      </div>
    </div>
  );
});

// ─── ColumnPicker ─────────────────────────────────────────────────────────────

const ColumnPicker = memo(({ all, visible, onToggle, onSelectAll, onReset }) => (
  <div className="bg-white border border-border rounded-xl p-5 mb-3">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm text-muted-foreground">
        Visible: <strong className="text-foreground">{visible.length}</strong> / {all.length}
      </span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onSelectAll}>All</Button>
        <Button variant="outline" size="sm" onClick={onReset}>Reset</Button>
      </div>
    </div>
    <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
      {all.map(col => {
        const on = visible.includes(col.key);
        return (
          <label
            key={col.key}
            className={cn(
              "flex items-center gap-2 cursor-pointer px-2.5 py-1.5 rounded-lg text-sm border transition-all",
              on
                ? "bg-violet-100 text-violet-700 border-violet-300"
                : "bg-muted/40 text-muted-foreground border-transparent hover:bg-muted"
            )}
          >
            <Checkbox
              checked={on}
              onCheckedChange={() => onToggle(col.key)}
              className={on ? "border-violet-400 data-[state=checked]:bg-violet-600 data-[state=checked]:border-violet-600" : ""}
            />
            {col.label}
          </label>
        );
      })}
    </div>
  </div>
));

// ─── SkuTableRow ──────────────────────────────────────────────────────────────

const SkuTableRow = memo(function SkuTableRow({ row, rowIndex, columns, isSelected, badgeCols, onToggleRow, onCellChange }) {
  return (
    <tr className={cn(
      "border-t border-border transition-colors",
      isSelected ? "bg-violet-50" : rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50/50",
      !isSelected && "hover:bg-slate-50"
    )}>
      <td className="px-3 py-2 text-center w-11">
        <Checkbox checked={isSelected} onCheckedChange={() => onToggleRow(row.id)} />
      </td>
      {columns.map(col => (
        <td key={col.key} className="px-3 py-2 text-sm align-middle overflow-hidden">
          {(() => {
            const rawValue = row[col.key];
            const displayValue = rawValue && typeof rawValue === "object" ? JSON.stringify(rawValue) : rawValue;
            if (badgeCols.has(col.key)) return <StatusBadge value={row[col.key]} />;
            if (col.editable === false || col.key.endsWith("_at")) {
              return (
                <span className="text-muted-foreground text-xs whitespace-nowrap">
                  {col.key.endsWith("_at") ? fmtDate(displayValue) : (displayValue ?? "—")}
                </span>
              );
            }
            return <EditableCell value={displayValue} rowId={row.id} colKey={col.key} onChange={onCellChange} />;
          })()}
        </td>
      ))}
    </tr>
  );
}, (prev, next) => (
  prev.row === next.row &&
  prev.rowIndex === next.rowIndex &&
  prev.columns === next.columns &&
  prev.isSelected === next.isSelected &&
  prev.onToggleRow === next.onToggleRow &&
  prev.onCellChange === next.onCellChange
));

// ─── SkuTable ─────────────────────────────────────────────────────────────────

const SkuTable = memo(({ columns, rows, loading, allChecked, selectedIds, onToggleAll, onToggleRow, onCellChange, sort, onSort, colWidths, onResizeStart }) => {
  const badgeCols = useMemo(() => new Set(["sku_status", "cost_audit", "attr_audit"]), []);
  const someSelected = rows.some(r => selectedIds[r.id]);

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-white">
      <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "calc(100vh - 340px)" }}>
        <table className="border-collapse w-full text-sm" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: 44 }} />
            {columns.map(col => <col key={col.key} style={{ width: colWidths[col.key] || col.width || 120 }} />)}
          </colgroup>
          <thead>
            <tr className="bg-slate-50 sticky top-0 z-10 border-b border-border">
              <th className="px-3 py-2.5 text-center w-11">
                <Checkbox
                  checked={allChecked ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={onToggleAll}
                />
              </th>
              {columns.map(col => {
                const sorted = sort?.key === col.key;
                return (
                  <th
                    key={col.key}
                    onClick={() => onSort(col.key)}
                    className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap cursor-pointer select-none relative"
                  >
                    <span className="flex items-center gap-1">
                      {col.label}
                      <SortIcon dir={sorted ? sort.dir : null} />
                    </span>
                    <ResizeHandle onMouseDown={e => onResizeStart(e, col.key)} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="py-12 text-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                  Loading SKU data…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="py-12 text-center text-muted-foreground text-sm">
                  No SKUs found. Try adjusting your filters.
                </td>
              </tr>
            ) : rows.map((row, ri) => (
              <SkuTableRow
                key={row.id}
                row={row}
                rowIndex={ri}
                columns={columns}
                isSelected={!!selectedIds[row.id]}
                badgeCols={badgeCols}
                onToggleRow={onToggleRow}
                onCellChange={onCellChange}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

// ─── SkuMaster (main page) ────────────────────────────────────────────────────

export default function SkuMaster() {
  const [rows,               setRows]               = useState([]);
  const [categories,         setCategories]         = useState([]);
  const [businessTypeOptions, setBusinessTypeOptions] = useState([]);
  const [skuTagOptions,      setSkuTagOptions]      = useState([]);
  const [loading,            setLoading]            = useState(false);
  const [count,              setCount]              = useState(0);
  const [page,               setPage]               = useState(1);
  const [pageSize,           setPageSize]           = useState(50);
  const [hasNextPage,        setHasNextPage]        = useState(false);
  const [hasPrevPage,        setHasPrevPage]        = useState(false);
  const [showActions,        setShowActions]        = useState(false);
  const [showDownloadPanel,  setShowDownloadPanel]  = useState(false);
  const [selectedIds,        setSelectedIds]        = useState({});
  const [toast,              setToast]              = useState(null);
  const [panel,              setPanel]              = useState(null); // "filters" | "columns" | null
  const [visibleKeys,        setVisibleKeys]        = useState(DEFAULT_VISIBLE);
  const [sort,               setSort]               = useState(null);
  const [colWidths,          setColWidths]          = useState({});
  const [downloadingFiltered, setDownloadingFiltered] = useState(false);
  const [downloadFilters,    setDownloadFilters]    = useState({ skuTag: "sellable", costAudit: "", attrAudit: "" });
  const [filters,            setFilters]            = useState({ ...DEFAULT_FILTERS });
  const [appliedFilters,     setAppliedFilters]     = useState({ ...DEFAULT_FILTERS });

  const visibleCols          = useMemo(() => TABLE_COLUMNS.filter(c => visibleKeys.includes(c.key)), [visibleKeys]);
  const visibleEditableKeys  = useMemo(() => visibleCols.filter(c => c.editable !== false).map(c => c.key), [visibleCols]);
  const selectedRows         = useMemo(() => rows.filter(r => selectedIds[r.id]), [rows, selectedIds]);
  const allChecked           = useMemo(() => rows.length > 0 && rows.every(r => selectedIds[r.id]), [rows, selectedIds]);
  const totalPagesFromCount  = Math.max(1, Math.ceil(count / pageSize));
  const totalPages           = hasNextPage ? Math.max(totalPagesFromCount, page + 1) : totalPagesFromCount;
  const hasActiveFilters     = Object.entries(appliedFilters).some(([k, v]) => k !== "compLevel" ? !!v : v);

  const showMessage = useCallback((message, type = "success") => setToast({ message, type }), []);

  // Column resize
  const resizeRef = useRef(null);
  const handleResizeStart = useCallback((e, key) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[key] || TABLE_COLUMNS.find(c => c.key === key)?.width || 120;
    resizeRef.current = { key, startX, startW };
    const onMove = mv => {
      const { key, startX, startW } = resizeRef.current;
      setColWidths(p => ({ ...p, [key]: Math.max(60, startW + mv.clientX - startX) }));
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [colWidths]);

  // Sort
  const handleSort = useCallback(key => {
    setSort(prev => {
      if (prev?.key === key) return prev.dir === "asc" ? { key, dir: "desc" } : null;
      return { key, dir: "asc" };
    });
    setPage(1);
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchSkus({ page, pageSize, filters: appliedFilters, sort });
      setRows(res.rows);
      setCount(res.count);
      setHasNextPage(Boolean(res.next));
      setHasPrevPage(Boolean(res.previous));
      setSelectedIds({});
    } catch (e) { showMessage(e.message || "Failed to load", "error"); }
    finally { setLoading(false); }
  }, [appliedFilters, page, pageSize, sort, showMessage]);

  const loadCategories = useCallback(async () => {
    try { setCategories(await fetchCategories()); }
    catch (e) { showMessage(e.message || "Failed to load categories", "error"); }
  }, [showMessage]);

  const loadFilterDropdowns = useCallback(async () => {
    try {
      const [businessTypes, skuTags] = await Promise.all([
        fetchDropdownOptions("business_type"),
        fetchDropdownOptions("sku_tag"),
      ]);
      setBusinessTypeOptions(businessTypes);
      setSkuTagOptions([{ v: "__blank__", l: "Blank" }, ...skuTags]);
    } catch (e) { showMessage(e.message || "Failed to load filter dropdown options", "error"); }
  }, [showMessage]);

  useEffect(() => { loadCategories(); },       [loadCategories]);
  useEffect(() => { loadFilterDropdowns(); },  [loadFilterDropdowns]);
  useEffect(() => { loadData(); },             [loadData]);

  // Download all filtered rows
  const handleDownloadFiltered = useCallback(async () => {
    setDownloadingFiltered(true);
    try {
      const filtersForDownload = { ...DEFAULT_FILTERS, skuTag: downloadFilters.skuTag, costAudit: downloadFilters.costAudit, attrAudit: downloadFilters.attrAudit };
      const all = []; let pg = 1, hasNext = true;
      while (hasNext) {
        const res = await fetchSkus({ page: pg, pageSize: 500, filters: filtersForDownload, sort: null });
        all.push(...res.rows); hasNext = Boolean(res.next); pg++;
        if (!res.rows.length) break;
      }
      if (!all.length) { showMessage("No rows to download.", "warning"); return; }
      downloadCsv(toCsv(all), `sku_filtered_${new Date().toISOString().slice(0, 10)}.csv`);
      showMessage(`Downloaded ${all.length} rows.`);
    } catch (e) { showMessage(e.message || "Download failed", "error"); }
    finally { setDownloadingFiltered(false); }
  }, [downloadFilters, showMessage]);

  // Cell edit
  const handleCellChange = useCallback((rowId, key, val) => {
    setRows(prev => {
      const i = prev.findIndex(r => r.id === rowId);
      if (i === -1) return prev;
      if (prev[i][key] === val) return prev;
      const next = [...prev]; next[i] = { ...prev[i], [key]: val }; return next;
    });
  }, []);

  // Bulk update selected rows
  const handleUpdateSelected = useCallback(async (rowsToUpdate) => {
    const payload = rowsToUpdate.filter(r => r?.id).map(r => {
      const obj = { id: r.id };
      visibleEditableKeys.forEach(k => { obj[k] = getBulkValue(k, r[k]); });
      return obj;
    });
    if (!payload.length) throw new Error("No valid row ids");
    const res = await bulkUpdateSkus(payload);
    if (res?.downloadedCsv) return { downloadedCsv: true };
    const body = res?.data || res || {};
    return { updated: Number(body.updated ?? body.updated_count ?? 0), failed: Number(body.failed ?? 0) };
  }, [visibleEditableKeys]);

  const toggleRow = useCallback(id => setSelectedIds(p => ({ ...p, [id]: !p[id] })), []);
  const toggleAll = useCallback(() => {
    if (allChecked) { setSelectedIds({}); return; }
    const n = {}; rows.forEach(r => { n[r.id] = true; }); setSelectedIds(n);
  }, [allChecked, rows]);

  const applyFilters = useCallback(() => {
    setAppliedFilters({ ...filters, search: filters.search.trim() });
    setPage(1);
  }, [filters]);
  const clearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS }); setAppliedFilters({ ...DEFAULT_FILTERS }); setPage(1);
  }, []);

  const togglePanel = k => setPanel(p => p === k ? null : k);

  // Page numbers around current page
  const pageNumbers = Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    return start + i;
  });

  return (
    <div className="min-h-screen bg-slate-50/70">
      <Navbar />

      {/* Custom scrollbar */}
      <style>{`
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {showActions && (
        <ActionsModal
          onClose={() => setShowActions(false)}
          selectedRows={selectedRows}
          onUpdateSelected={handleUpdateSelected}
          onUploadComplete={() => { setShowActions(false); loadData(); }}
          showMessage={showMessage}
        />
      )}

      <div className="max-w-[1600px] mx-auto px-5 py-6">

        {/* ── Page header ── */}
        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">SKU Master</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage and update SKU records across all business types
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <Button
              variant="outline"
              onClick={() => setShowDownloadPanel(prev => !prev)}
              disabled={downloadingFiltered}
              className="gap-2"
            >
              {downloadingFiltered && <Loader2 className="h-4 w-4 animate-spin" />}
              {downloadingFiltered ? "Downloading…" : "Download"}
            </Button>
            <Button
              onClick={() => { setShowDownloadPanel(false); setShowActions(true); }}
            >
              Actions
            </Button>
          </div>
        </div>

        {/* ── Download panel ── */}
        {showDownloadPanel && (
          <div className="bg-white border border-border rounded-xl p-4 mb-3 grid gap-3 items-end" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            {/* SKU Tag */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">SKU Tag</label>
              <select value={downloadFilters.skuTag} onChange={e => setDownloadFilters(p => ({ ...p, skuTag: e.target.value }))} className={SELECT_CLS}>
                <option value="all">all</option>
                {skuTagOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            {/* Cost Audit */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Cost Audit</label>
              <select value={downloadFilters.costAudit} onChange={e => setDownloadFilters(p => ({ ...p, costAudit: e.target.value }))} className={SELECT_CLS}>
                <option value="">All</option>
                {AUDIT_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            {/* Attr Audit */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Attr Audit</label>
              <select value={downloadFilters.attrAudit} onChange={e => setDownloadFilters(p => ({ ...p, attrAudit: e.target.value }))} className={SELECT_CLS}>
                <option value="">All</option>
                {AUDIT_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            {/* Actions */}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setDownloadFilters({ skuTag: "", costAudit: "", attrAudit: "" })} disabled={downloadingFiltered}>
                Clear
              </Button>
              <Button size="sm" onClick={handleDownloadFiltered} disabled={downloadingFiltered} className="gap-1">
                {downloadingFiltered && <Loader2 className="h-3 w-3 animate-spin" />}
                {downloadingFiltered ? "Downloading…" : "Download CSV"}
              </Button>
            </div>
          </div>
        )}

        {/* ── Toolbar ── */}
        <div className="bg-white border border-border rounded-xl px-4 py-3 mb-3 flex items-center gap-2.5 flex-wrap">
          <Input
            className="flex-1 min-w-[220px]"
            placeholder="Search SKU, name, parent, ref…"
            value={filters.search}
            onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
            onKeyDown={e => { if (e.key === "Enter") applyFilters(); }}
          />

          <Button
            variant={panel === "filters" ? "default" : "outline"}
            size="sm"
            onClick={() => togglePanel("filters")}
          >
            {`Filters${hasActiveFilters ? " ●" : ""}`}
          </Button>

          <Button
            variant={panel === "columns" ? "default" : "outline"}
            size="sm"
            onClick={() => togglePanel("columns")}
          >
            Columns
          </Button>

          <Button variant="outline" size="sm" onClick={applyFilters}>
            Search
          </Button>

          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer"
          >
            {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s} / page</option>)}
          </select>
        </div>

        {/* ── Panels ── */}
        {panel === "filters" && (
          <FilterPanel
            filters={filters}
            setFilters={setFilters}
            categories={categories}
            businessTypeOptions={businessTypeOptions}
            skuTagOptions={skuTagOptions}
            onApply={applyFilters}
            onClear={clearFilters}
          />
        )}
        {panel === "columns" && (
          <ColumnPicker
            all={TABLE_COLUMNS}
            visible={visibleKeys}
            onToggle={key => setVisibleKeys(p => {
              if (p.includes(key)) {
                if (p.length === 1) { showMessage("At least one column must be visible.", "warning"); return p; }
                return p.filter(k => k !== key);
              }
              return [...p, key];
            })}
            onSelectAll={() => setVisibleKeys(TABLE_COLUMNS.map(c => c.key))}
            onReset={() => { setVisibleKeys(DEFAULT_VISIBLE); showMessage("Columns reset.", "success"); }}
          />
        )}

        {/* ── Table meta bar ── */}
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              <strong className="text-foreground">{count.toLocaleString()}</strong> total
            </span>
            {selectedRows.length > 0 && (
              <>
                <span className="text-sm text-primary font-medium">{selectedRows.length} selected</span>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setSelectedIds({})}>
                  Clear selection
                </Button>
              </>
            )}
          </div>
          {sort && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              Sorted by <strong className="text-foreground mx-1">{sort.key}</strong> {sort.dir}
              <button onClick={() => setSort(null)} className="ml-1 text-primary hover:underline text-xs">
                ✕ clear
              </button>
            </span>
          )}
        </div>

        {/* ── Table ── */}
        <SkuTable
          columns={visibleCols}
          rows={rows}
          loading={loading}
          allChecked={allChecked}
          selectedIds={selectedIds}
          onToggleAll={toggleAll}
          onToggleRow={toggleRow}
          onCellChange={handleCellChange}
          sort={sort}
          onSort={handleSort}
          colWidths={colWidths}
          onResizeStart={handleResizeStart}
        />

        {/* ── Pagination ── */}
        <div className="flex items-center justify-between mt-4 py-3 flex-wrap gap-3">
          <span className="text-sm text-muted-foreground">
            Page{" "}
            <strong className="text-foreground">{page}</strong>{" "}
            of{" "}
            <strong className="text-foreground">{totalPages}</strong>
            <span className="ml-2 text-muted-foreground/60">({count.toLocaleString()} rows)</span>
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="px-2" onClick={() => setPage(1)} disabled={!hasPrevPage}>«</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={!hasPrevPage}>‹ Prev</Button>

            {pageNumbers.map(n => (
              <Button
                key={n}
                variant={n === page ? "default" : "outline"}
                size="sm"
                className="w-8 px-0"
                onClick={() => setPage(n)}
              >
                {n}
              </Button>
            ))}

            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={!hasNextPage}>Next ›</Button>
            <Button variant="outline" size="sm" className="px-2" onClick={() => setPage(p => p + 1)} disabled={!hasNextPage}>»</Button>
          </div>
        </div>

      </div>
    </div>
  );
}
